import { isPendingReport } from "~/helpers/reportStatus.helper";
import type { ReportData } from "~/types/global";

// The API closes the status socket when it goes idle; re-dialling with nothing
// in flight turned that into a permanent loop.
let explicitWorkUntil = 0;

/** Marks work that only arrives over this socket. */
export const markStatusSocketWork = (ttlMs = 10 * 60_000): void => {
    explicitWorkUntil = Date.now() + ttlMs;
};

export const clearStatusSocketWork = (): void => {
    explicitWorkUntil = 0;
};

export const hasExplicitStatusSocketWork = (now = Date.now()): boolean => now < explicitWorkUntil;

/**
 * Server-reported pending work, read from cached report payloads. Only the API
 * knows what is really running; this is the freshest evidence the client holds,
 * and unlike the in-memory stores it survives a reload.
 */
export const hasPendingWorkInSnapshots = (reports: unknown[]): boolean =>
    reports.some((report) => {
        if (!report || typeof report !== "object") return false;
        if ((report as { summary_generating?: boolean }).summary_generating === true) return true;
        return isPendingReport(report as ReportData);
    });

export interface StatusSocketReconnectInput {
    isAuthorized: boolean;
    hasStreamingProgress: boolean;
    hasSectionPreview: boolean;
    isGeneratingSummary: boolean;
    hasServerPendingWork: boolean;
    hasExplicitWork: boolean;
}

export const shouldReconnectStatusSocket = ({
    isAuthorized,
    hasStreamingProgress,
    hasSectionPreview,
    isGeneratingSummary,
    hasServerPendingWork,
    hasExplicitWork,
}: StatusSocketReconnectInput): boolean => {
    if (!isAuthorized) return false;
    return (
        hasStreamingProgress ||
        hasSectionPreview ||
        isGeneratingSummary ||
        hasServerPendingWork ||
        hasExplicitWork
    );
};
